import styled from '@emotion/styled';
import { Box, Button, FormControl, FormHelperText, TextField, Tooltip, Typography } from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { createElement, FC, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Prompt } from 'react-router';

import { ProjectForCreationSetDetailFragment } from '../../../../graphql/generated/graphql';
import useInputComment, { inputCommentLimit } from '../../../../hooks/useInputComment';
import theme from '../../../../theme';
import { SPACING_UNIT } from '../../../constant/spacing';
import { context as StateContext } from '../../../context/state';

interface Props {
  project: Pick<ProjectForCreationSetDetailFragment, 'id' | 'canIssueComment'> & {
    issue?: Pick<NonNullable<ProjectForCreationSetDetailFragment['issue']>, 'id'> | null;
  };
  className?: string;
  onSubmitProjectIssue: (projectId: string, inputComment: string) => Promise<void>;
  onSubmitProjectIssueComment: (issueId: string, comment: string) => void;
}

const ProjectIssueForm: FC<Props> = ({ project, className, onSubmitProjectIssue, onSubmitProjectIssueComment }) => {
  const [inputComment, commentHandler] = useInputComment('');
  const state = useContext(StateContext);

  useEffect(() => {
    const setDeleteConfirmAlert = (e: BeforeUnloadEvent) => {
      if (inputComment.body.length > 0) {
        e.preventDefault(); // for IE and Edge
        e.returnValue = 'alert';
      }
    };

    window.addEventListener('beforeunload', setDeleteConfirmAlert);
    return () => {
      window.removeEventListener('beforeunload', setDeleteConfirmAlert);
    };
  }, [inputComment]);

  return (
    <Root className={className} id={'issue.id'}>
      <Heading>
        <Title>
          <FormattedMessage id="project_detail_page.project_issue.input_comment.title" />
        </Title>
        {state.language == 'ja-JP' ? (
          <FormattedMessage id="project_detail_page.project_issue.input_comment.help">
            {(text: string) => (
              <Tooltip placement="top" title={text}>
                <HelpAnchor
                  href="https://help.kaizen-ad.com/ja/articles/5362199-初稿納品前コメント機能について"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Help />
                </HelpAnchor>
              </Tooltip>
            )}
          </FormattedMessage>
        ) : null}
      </Heading>
      {state.language == 'ja-JP' ? (
        <CommentFormInfo>
          <FormattedMessage id="project_detail_page.project_issue.input_comment.info" />
        </CommentFormInfo>
      ) : null}
      <FormArea>
        {!project.canIssueComment ? (
          <CommentForm error={inputComment.error}>
            <DisabledComment>
              <DisabledMessage>
                <FormattedMessage id="project_detail_page.project_issue.input_comment.disabled" />
              </DisabledMessage>
            </DisabledComment>
            <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
          </CommentForm>
        ) : (
          <CommentForm error={inputComment.error}>
            <Comment
              label={
                <Placeholder align="center">
                  <FormattedMessage id="project_detail_page.project_issue.input_comment.label" />
                </Placeholder>
              }
              value={inputComment.body}
              variant="outlined"
              rows="4"
              rowsMax="8"
              multiline
              error={inputComment.error}
              onChange={(e) => commentHandler.setComment(e.target.value)}
            />
            <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
          </CommentForm>
        )}

        <Buttons>
          <Submit
            disabled={!project.canIssueComment || inputComment.body.trim() === '' || inputComment.error}
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              commentHandler.setComment('');
              project.issue
                ? onSubmitProjectIssueComment(project.issue.id, inputComment.body)
                : onSubmitProjectIssue(project.id, inputComment.body);
            }}
          >
            <FormattedMessage id="project_detail_page.project_issue.input_comment.submit" />
          </Submit>
        </Buttons>
      </FormArea>
      <FormattedMessage id="project_detail_page.confirm_alert.text">
        {(text: string) => <Prompt when={inputComment.body.length > 0} message={text} />}
      </FormattedMessage>
    </Root>
  );
};

const Root = styled('div')({});

const Heading = styled('div')({
  marginBottom: SPACING_UNIT,
  display: 'flex',
});

const Title = styled(Typography)({});
Title.defaultProps = { variant: 'h6', color: 'textSecondary' };

const CommentFormInfo = styled(Typography)({
  marginBottom: SPACING_UNIT,
  whiteSpace: 'pre-wrap',
});
CommentFormInfo.defaultProps = { color: 'textSecondary' };

const HelpAnchor = styled('a')({
  marginLeft: SPACING_UNIT,
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.grey['500'],
  textDecoration: 'none',
});

const FormArea = styled('div')({
  width: '100%',
});

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_UNIT,
});

const Comment = styled(TextField)({
  width: '100%',
});

const Placeholder = styled(Typography)({
  color: `${theme.palette.grey['400']}`,
});

const InputCount = styled(FormHelperText)({
  textAlign: 'right',
});

const Buttons = styled('div')({
  textAlign: 'right',
});
const Submit = styled(Button)({
  width: 'auto',
});

const DisabledComment = styled(Box)({
  borderRadius: 4,
  border: '1px solid rgba(0, 0, 0, 0.12)',
  height: '113px',
  width: '100%',
  background: `#CBD5E0`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
});
const DisabledMessage = styled(Typography)({
  fontSize: '18px',
});

export default ProjectIssueForm;
